import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { useSocketConnectionId } from '@contexts/SocketContext';
import { Checkbox, TextField } from '@mui/material';
import { dashboardService } from '@services/dashboardService';
import type { Dashboard } from '@/types/dashboard';
import { Modal } from '@components/utils/Modal';
import { getLayersToHide } from '@components/utils/CorgeeHelper';
import { ReportDatePicker } from '@components/Reports/ReportDatePicker';

interface EditDashboardModalProps {
  dashboard?: Dashboard;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onSave?: (updated: Dashboard) => void;
  // When false, name and public flag are read-only. Defaults to true.
  canEdit?: boolean;
}

export const EditDashboardModal: React.FC<any> = ({
  dashboard,
  open,
  setOpen,
  onSave,
  canEdit = true,
}: EditDashboardModalProps) =>
{
  const socketConnectionId = useSocketConnectionId();
  const initialized = useRef(false);

  const [dashboardName, setDashboardName] = useState<string>('');
  const [checkboxIsPublic, setCheckboxIsPublic] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<string>('');
  const [dueDate, setDueDate] = useState<string>('');
  const [checkboxFilDataValue, setCheckboxFilDataValue] = useState<boolean>(false);

  const [warring, setWarring] = useState<string>('');

  useEffect(() =>
  {
    const missingFields: string[] = [];
    if (!startDate) missingFields.push('Start Date');
    if (!dueDate) missingFields.push('Due Date');

    if (missingFields.length > 0)
    {
      setWarring(
        `${missingFields.join(' and ')} ${missingFields.length === 1 ? 'is' : 'are'} required`,
      );
    }
    else if (moment(startDate, 'YYYY-MM-DD').isAfter(moment(dueDate, 'YYYY-MM-DD')))
    {
      setWarring('The start date cannot be greater than the due date');
    }
    else
    {
      setWarring('');
    }
  }, [startDate, dueDate]);

  useEffect(() =>
  {
    if (open)
    {
      if (dashboard && !initialized.current)
      {
        setDashboardName(dashboard.Name);
        setCheckboxIsPublic(dashboard.IsPublic);
        // Dashboard dates come as MM/DD/YYYY from API; convert to YYYY-MM-DD for ReportDatePicker
        const today = moment().format('YYYY-MM-DD');
        const parsedStart = dashboard.StartDate
          ? moment(dashboard.StartDate, ['MM/DD/YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD', moment.ISO_8601])
          : null;
        setStartDate(parsedStart?.isValid() ? parsedStart.format('YYYY-MM-DD') : today);
        const parsedDue = dashboard.DueDate
          ? moment(dashboard.DueDate, ['MM/DD/YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD', moment.ISO_8601])
          : null;
        setDueDate(parsedDue?.isValid() ? parsedDue.format('YYYY-MM-DD') : today);
        setCheckboxFilDataValue((dashboard as any).checkboxFilDataValue ?? false);
        setWarring('');
        initialized.current = true;
      }
    }
    else
    {
      initialized.current = false;
    }
  }, [open]);

  const submit = async (): Promise<void> =>
  {
    const missingFields: string[] = [];
    if (!startDate) missingFields.push('Start Date');
    if (!dueDate) missingFields.push('Due Date');
    if (missingFields.length > 0)
    {
      setWarring(
        `${missingFields.join(' and ')} ${missingFields.length === 1 ? 'is' : 'are'} required`,
      );
      return;
    }

    if (moment(startDate, 'YYYY-MM-DD').isAfter(moment(dueDate, 'YYYY-MM-DD')))
    {
      setWarring('The start date cannot be greater than the due date');
      return;
    }

    if (dashboard && dashboard.DashboardId > 0)
    {
      // Only creator/admin can change name and public flag.
      if (canEdit)
      {
        dashboard.Name = dashboardName;
        dashboard.IsPublic = checkboxIsPublic;
      }
      dashboard.SocketConnectionId = socketConnectionId;
      dashboard.StartDate = startDate ? moment(startDate, 'YYYY-MM-DD').format('MM/DD/YYYY') : '';
      dashboard.DueDate = dueDate ? moment(dueDate, 'YYYY-MM-DD').format('MM/DD/YYYY') : '';

      try
      {
        await dashboardService.UpdateDashboard(dashboard!);
        onSave?.(dashboard);
      }
      catch (error)
      {
        setWarring(`Something went wrong: ${error}`);
        return;
      }

      if (checkboxFilDataValue)
      {
        const request = {
          StartDate: dashboard.StartDate,
          DueDate: dashboard.DueDate,
          EndDate: moment().format('DD/MM/YYYY'),
          CustomFields: [],
          DashboardId: dashboard.DashboardId,
          Project: dashboard.ProjectId,
          Filters: '[]',
          SearchText: '',
          LayersToHide: getLayersToHide().join(),
          PageNumber: 1,
          PageSize: 100,
        };
        try
        {
          await dashboardService.UpdateDashboardHistorical(request);
        }
        catch (error)
        {
          setWarring(`Something went wrong: ${error}`);
        }
      }
      setOpen(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => setOpen(false)}
      title="Edit dashboard"
      preventCloseClickOut
      className="self-start mt-16"
    >
      <div className="min-w-[300px] w-[306px] max-w-[306px] flex flex-col">
        <div className="pt-8">
          <TextField
            label="Dashboard Name"
            variant="standard"
            onChange={e => setDashboardName(e.target.value)}
            value={dashboardName}
            fullWidth
            disabled={!canEdit}
            slotProps={{
              inputLabel: {
                shrink: true,
                sx: {
                  fontWeight: 600,
                  color: '#414042',
                  fontSize: '16px',
                  '&.Mui-focused': {
                    color: '#414042',
                  },
                },
              },
              input: {
                readOnly: !canEdit,
                disableUnderline: false,
                sx: {
                  '&:before': {
                    borderBottom: '1px solid #414042',
                    transition: 'none',
                  },
                  '&:hover:not(.Mui-disabled):before': {
                    borderBottom: '1px solid #414042',
                    transition: 'none',
                  },
                  '&:after': {
                    borderBottom: '1px solid #414042',
                    transition: 'none',
                  },
                },
              },
            }}
          />
        </div>
        {canEdit ? (
          <div className="flex flex-row pt-9">
            <Checkbox
              checked={checkboxIsPublic}
              disableTouchRipple
              disableFocusRipple
              disableRipple
              onChange={e => setCheckboxIsPublic(e.target.checked)}
              sx={{
                paddingLeft: 0,
                '&.Mui-checked': {
                  color: '#62accb',
                },
              }}
            />
            <span className="text-xs leading-[42px]">Is Public?</span>
          </div>
        ) : (
          <div className="pt-4">
            <span className="text-[#e14949] text-xs font-medium">
              Only dashboard creator can change permissions.
            </span>
          </div>
        )}
        <div className="pt-6 flex flex-col">
          <span className="mb-4 py-0.5 text-[12px] font-sans text-[#414042] font-semibold">
            Fill Data Process
          </span>
          <span className="mb-[10px] text-[12px] text-wrap">
            Select a date range and enable the Fill Data option to update the Estimate and Remaining
            details using historical data.
          </span>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-1 w-full [&_.react-datepicker-wrapper]:flex-1 [&_.ar-date-input-wrapper]:w-full">
              <span className="text-[11px] text-[#666] w-[62px] flex-shrink-0">Start Date</span>
              <ReportDatePicker value={startDate} onChange={setStartDate} clearable />
            </div>
            <div className="flex items-center gap-1 w-full [&_.react-datepicker-wrapper]:flex-1 [&_.ar-date-input-wrapper]:w-full">
              <span className="text-[11px] text-[#666] w-[62px] flex-shrink-0">Due Date</span>
              <ReportDatePicker value={dueDate} onChange={setDueDate} clearable />
            </div>
          </div>
        </div>
        {warring.length > 0 && (
          <div className="mt-2 mb-0.5">
            <span className="text-[#e14949] text-xs font-medium">{warring}</span>
          </div>
        )}
        <div className="flex flex-row pt-[15px]">
          <Checkbox
            checked={checkboxFilDataValue}
            disableTouchRipple
            disableFocusRipple
            disableRipple
            onChange={e => setCheckboxFilDataValue(e.target.checked)}
            sx={{
              paddingLeft: 0,
              '&.Mui-checked': {
                color: '#62accb',
              },
            }}
          />
          <span className="text-xs leading-[42px]">Fill Data</span>
        </div>
        <div className="pt-3 text-right gap-[10px] relative overflow-hidden min-h-6 flex flex-row-reverse">
          <button
            type="button"
            onClick={() =>
            {
              submit();
            }}
            className="bg-corgee-blue hover:bg-corgee-BlueHover border text-white text-xs h-7 px-3 py-1"
          >
            Save
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="bg-brightGray hover:bg-corgee-brightGrayHover border text-white text-xs h-7 px-3 py-1"
          >
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
};
